import classNames from 'classnames';
import { ComponentProps, createRef, forwardRef, type JSX, ReactNode, Ref, useEffect } from 'react';
import ReportLocation from '@alltrails/amplitude/enums/ReportLocation';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import type { LightboxPhoto, LightboxUser } from '@alltrails/denali/types';
import type Context from '@alltrails/types/Context';
import type { Review } from '@alltrails/types/Review';
import type { ReviewPhoto } from '@alltrails/types/ReviewPhoto';
import type { User } from '@alltrails/types/User';
import ReviewItem from '../ReviewItem/ReviewItem';
import ShowMore from '../ShowMore/ShowMore';
import { TrailReview } from '../TrailReview';
import styles from './ReviewsBase.module.scss';

export type ReviewsBaseProps = {
  ads?: ReactNode[];
  canLoadMore?: boolean;
  context: Context;
  emptyState?: ReactNode;
  fullWidthShowMore?: boolean;
  includeTrailName?: boolean;
  infiniteScroll?: boolean;
  isLoadingMore?: boolean;
  localizeOnLoad?: boolean;
  onActivityLinkClick: (review: Review) => void;
  onDeleteReview?: (review: Review) => void;
  onLightboxClose?: () => void;
  onLightboxPhotoViewed?: (review: Review, photo: LightboxPhoto['photo']) => void;
  onLightboxUserClick?: (user: LightboxUser) => void;
  onLoadMore: () => void;
  onReviewPhotoClick?: (review: Review, photo: ReviewPhoto) => void;
  onReviewPhotosScroll?: (review: Review, numPhotos: number) => void;
  onReviewPhotosScrollEnd?: (review: Review, numPhotos: number) => void;
  onToggleSummaryExclusion?: (review: Review, summaryExclusion: boolean) => void;
  onUpdateReview?: (data: { detail: Review }) => void;
  onUserClick?: (user: User) => void;
  openReviewFormModal?: (trailFormProps: any) => void;
  photosClassName?: string;
  preferHTML?: boolean;
  reportLocation?: ReportLocation;
  resultsFooter?: ReactNode;
  ReviewRenderer?: (props: ComponentProps<typeof TrailReview>) => JSX.Element;
  reviews: Review[];
  total: number;
  trailLinkNewTab?: boolean;
  photosOverflowOnMobile?: boolean;
  bottomLinkType?: 'trail' | 'activity';
  onTrailLinkClick?: () => void;
  className?: string;
};

/**
 * An uncontrolled reviews component.
 */
const ReviewsBase = forwardRef(
  (
    {
      ads,
      canLoadMore,
      context,
      emptyState,
      fullWidthShowMore,
      includeTrailName,
      infiniteScroll,
      isLoadingMore,
      localizeOnLoad,
      onActivityLinkClick,
      onDeleteReview,
      onLightboxClose,
      onLightboxPhotoViewed,
      onLightboxUserClick,
      onLoadMore,
      onReviewPhotoClick,
      onReviewPhotosScroll,
      onReviewPhotosScrollEnd,
      onToggleSummaryExclusion,
      onUpdateReview,
      onUserClick,
      openReviewFormModal,
      preferHTML,
      photosClassName,
      reportLocation,
      resultsFooter,
      reviews,
      ReviewRenderer,
      total,
      trailLinkNewTab,
      photosOverflowOnMobile,
      bottomLinkType = 'activity',
      onTrailLinkClick,
      className
    }: ReviewsBaseProps,
    ref: Ref<HTMLDivElement>
  ): JSX.Element => {
    const lastSection = createRef<HTMLDivElement>();
    const isLastSectionVisible = useIsVisible(lastSection);

    /**
     * Support for infinite scrolling
     */
    useEffect(() => {
      if (infiniteScroll && isLastSectionVisible && reviews.length && reviews.length < total) {
        onLoadMore();
      }
    }, [infiniteScroll, isLastSectionVisible, onLoadMore, reviews.length, total]);

    return (
      <div ref={ref} tabIndex={-1} className={classNames(styles.stickyWrapper, className)}>
        {!!reviews.length &&
          reviews.map((review, idx) => (
            <div key={review.id} ref={idx === reviews.length - 1 ? lastSection : undefined}>
              <ReviewItem
                analyticsReportLocation={reportLocation}
                context={context}
                includeTrailName={includeTrailName}
                index={idx}
                languageRegionCode={context.languageRegionCode}
                localizeOnLoad={localizeOnLoad}
                onActivityLinkClick={onActivityLinkClick}
                onDeleteReview={onDeleteReview}
                onLightboxClose={onLightboxClose}
                onLightboxPhotoViewed={onLightboxPhotoViewed}
                onLightboxUserClick={onLightboxUserClick}
                onReviewPhotoClick={onReviewPhotoClick}
                onReviewPhotosScroll={onReviewPhotosScroll}
                onReviewPhotosScrollEnd={onReviewPhotosScrollEnd}
                onToggleSummaryExclusion={onToggleSummaryExclusion}
                onUpdateReview={onUpdateReview}
                onUserClick={onUserClick}
                openReviewFormModal={openReviewFormModal}
                preferHTML={preferHTML}
                photosClassName={photosClassName}
                ReviewRenderer={ReviewRenderer}
                review={review}
                trailLinkNewTab={trailLinkNewTab}
                photosOverflowOnMobile={photosOverflowOnMobile}
                bottomLinkType={bottomLinkType}
                onTrailLinkClick={onTrailLinkClick}
              />
              {idx === 4 && ads?.[0]}
              {idx === 14 && ads?.[1]}
              {idx === 24 && ads?.[2]}
            </div>
          ))}
        {!reviews.length && emptyState}
        {resultsFooter}
        {!infiniteScroll && canLoadMore && <ShowMore isLoading={!!isLoadingMore} onLoadMore={onLoadMore} fullWidth={fullWidthShowMore} />}
      </div>
    );
  }
);

export default ReviewsBase;
